// Dynamically determine API URL (same logic as config.js)
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === "localhost" || hostname === "127.0.0.1") {
    return "http://localhost:8800";
  }
  if (hostname.match(/^(192\.168|10\.|172\.)\d+\.\d+/)) {
    return `http://${hostname}:8800`;
  }
  return "https://clipbait-app.onrender.com";
};

const API = getApiUrl();

const CLIPS_BASE = `${API}/api/clips`;
const COMMENTS_BASE = `${API}/api/comments`;

/* ---------------- helpers ---------------- */
function enc(x) {
  return encodeURIComponent(x);
}

/* read the logged-in user from localStorage (optional legacy) */
function getCurrentUser() {
  try {
    const raw =
      localStorage.getItem("user") ||
      localStorage.getItem("currentUser") ||
      localStorage.getItem("profile");
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

/* try to read a JWT if you store one (optional) */
function getAuthToken() {
  try {
    return (
      localStorage.getItem("authToken") ||
      localStorage.getItem("token") ||
      localStorage.getItem("accessToken") ||
      localStorage.getItem("jwt")
    ) || null;
  } catch {
    return null;
  }
}

/* Best-effort client-side normalization (defensive only) */
function normalizeName(x) {
  let s = String(x || "").trim();
  s = s.replace(/^@+/, "").replace(/[ :,\-–—•]+$/, "");
  s = s.replace(/\s+/g, " ");
  const m1 = s.match(/^\s*(.+?)\s*,\s*\1\s*$/i);
  if (m1) s = m1[1];
  const m2 = s.match(/^\s*(.+?)\s+\1\s*$/i);
  if (m2) s = m2[1];
  return s.slice(0, 60) || "Anon";
}

/* Canonicalize header key casing to avoid duplicates (Node lowercases incoming headers). */
function lowerCaseKeys(obj = {}) {
  const out = {};
  for (const k of Object.keys(obj || {})) out[k.toLowerCase()] = obj[k];
  return out;
}

/* Attach dev auth headers if we have a user in localStorage. */
function devAuthHeaders() {
  const u = getCurrentUser();
  if (!u) return {};
  const id = u._id || u.id || u.userId || (u.user && (u.user._id || u.user.id));
  if (!id) return {};

  const name =
    u.username ||
    u.name ||
    u.displayName ||
    u.email ||
    (u.user && (u.user.username || u.user.name || u.user.email)) ||
    "Anon";

  const clean = normalizeName(name);
  return {
    "x-user-id": String(id),
    "x-username": clean,
  };
}

/* Allow explicitly passing a userId per-call (preferred) — canonical lowercase only */
function userHeaders(userId) {
  return userId ? { "x-user-id": String(userId) } : {};
}

/* Build final headers for requests (no duplicate keys by case) */
function buildHeaders(extra = {}, userId = null) {
  const token = getAuthToken();
  const extraL = lowerCaseKeys(extra);
  const baseL = lowerCaseKeys({ ...devAuthHeaders(), ...userHeaders(userId) });
  const headers = { ...baseL, ...extraL };
  if (token && !("authorization" in headers)) {
    headers["authorization"] = `Bearer ${token}`;
  }
  return headers;
}

/* Unified fetch that always includes credentials + dev headers (+ optional Bearer) */
async function jsonFetch(url, opts = {}, userId = null) {
  const mergedHeaders = buildHeaders(opts.headers || {}, userId);
  const res = await fetch(url, {
    credentials: "include",
    ...opts,
    headers: mergedHeaders,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {}
  if (!res.ok) {
    const msg =
      (data && (data.error || data.message)) ||
      `${res.status} ${res.statusText}`;
    const err = new Error(msg);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

/* ---------------- clips ---------------- */

export async function upsertClip(payload, { userId } = {}) {
  return jsonFetch(
    `${CLIPS_BASE}/upsert`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(payload),
    },
    userId
  );
}

export async function toggleLike(clipKey, { userId } = {}) {
  try {
    return await jsonFetch(
      `${CLIPS_BASE}/toggle-like`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ clipKey }),
      },
      userId
    );
  } catch {
    return jsonFetch(
      `${CLIPS_BASE}/${enc(clipKey)}/like`,
      { method: "POST" },
      userId
    );
  }
}

export async function bulkLikedStatus(clipKeys, { userId } = {}) {
  return jsonFetch(
    `${CLIPS_BASE}/likes/bulk`,
    {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ clipKeys }),
    },
    userId
  );
}

/* ---------------- comments ---------------- */

export async function fetchComments(
  clipKey,
  { limit = 20, cursor } = {},
  { userId } = {}
) {
  const q = new URLSearchParams();
  if (typeof limit === "number") q.set("limit", String(limit));
  if (cursor) q.set("cursor", cursor);

  try {
    const j = await jsonFetch(
      `${COMMENTS_BASE}/${enc(clipKey)}?${q.toString()}`,
      {},
      userId
    );
    const list = Array.isArray(j) ? j : j.comments || j.items || j.data || [];
    const count = typeof j.count === "number" ? j.count : list.length;
    return { comments: list, count, nextCursor: j.nextCursor };
  } catch {
    const legacy = await jsonFetch(
      `${CLIPS_BASE}/${enc(clipKey)}/comments?limit=${limit}`,
      {},
      userId
    );
    const list = Array.isArray(legacy)
      ? legacy
      : legacy.comments || legacy.items || legacy.data || [];
    const count = typeof legacy.count === "number" ? legacy.count : list.length;
    return { comments: list, count, nextCursor: legacy.nextCursor };
  }
}

export async function fetchCommentCount(clipKey, opts = {}, extra = {}) {
  const { count } = await fetchComments(clipKey, { ...opts, limit: 0 }, extra);
  return count;
}

export async function postComment(
  clipKey,
  text,
  parentId = null,
  { userId } = {}
) {
  const body = JSON.stringify({ text, parentId });
  try {
    return await jsonFetch(
      `${COMMENTS_BASE}/${enc(clipKey)}`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
      },
      userId
    );
  } catch {
    return await jsonFetch(
      `${CLIPS_BASE}/${enc(clipKey)}/comments`,
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
      },
      userId
    );
  }
}

export async function toggleCommentLike(clipKey, commentId, { userId } = {}) {
  try {
    return await jsonFetch(
      `${COMMENTS_BASE}/by-id/${enc(commentId)}/toggle-like`,
      { method: "POST" },
      userId
    );
  } catch {
    try {
      return await jsonFetch(
        `${COMMENTS_BASE}/${enc(clipKey)}/${enc(commentId)}/toggle-like`,
        { method: "POST" },
        userId
      );
    } catch {
      return await jsonFetch(
        `${CLIPS_BASE}/${enc(clipKey)}/comments/${enc(commentId)}/like`,
        { method: "POST" },
        userId
      );
    }
  }
}

/**
 * Delete a specific comment (and likely its replies server-side).
 * Preferred:  DELETE /api/comments/by-id/:commentId
 * Fallback:   DELETE /api/comments/:clipKey/:commentId
 * Legacy:     DELETE /api/clips/:clipKey/comments/:commentId
 */
export async function deleteComment(clipKey, commentId, { userId } = {}) {
  try {
    return await jsonFetch(
      `${COMMENTS_BASE}/by-id/${enc(commentId)}`,
      { method: "DELETE" },
      userId
    );
  } catch {
    try {
      return await jsonFetch(
        `${COMMENTS_BASE}/${enc(clipKey)}/${enc(commentId)}`,
        { method: "DELETE" },
        userId
      );
    } catch {
      return await jsonFetch(
        `${CLIPS_BASE}/${enc(clipKey)}/comments/${enc(commentId)}`,
        { method: "DELETE" },
        userId
      );
    }
  }
}

/* Optional alias some code may still call */
export async function addComment(clipKey, bodyText, opts = {}) {
  return postComment(clipKey, bodyText, null, opts);
}
